import * as SliderPrimitive from '@radix-ui/react-slider';
import { useTranslations } from 'next-intl';
import type { SubscriptionProductType } from '@/app/types';
import { cn } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import type { UltimateProductOption } from '../utils';

export const UltimateProductSlider = ({
  options,
  selectedProduct,
  onChange,
  disabled,
}: {
  options: UltimateProductOption[];
  selectedProduct: SubscriptionProductType;
  onChange: (product: SubscriptionProductType) => void;
  disabled?: boolean;
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.product.id === selectedProduct.id),
  );
  const creditsLabel = (option: UltimateProductOption) => option.credits.toLocaleString(locale);

  return (
    <div className="mb-2 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 text-xs">
        {options.map((option) => (
          <button
            key={option.product.id}
            type="button"
            disabled={disabled}
            aria-pressed={option.product.id === selectedProduct.id}
            aria-label={`${creditsLabel(option)} ${t('credits')}`}
            className={cn(
              'rounded-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9B93FF]',
              option.product.id === selectedProduct.id ? 'font-medium text-system-white' : 'text-system-white48',
            )}
            onClick={() => onChange(option.product)}
          >
            {creditsLabel(option)}
          </button>
        ))}
      </div>
      <SliderPrimitive.Root
        className="relative flex h-5 w-full touch-none select-none items-center [&>span]:transition-[left] [&>span]:duration-[450ms] [&>span]:ease-[cubic-bezier(0.34,1.56,0.64,1)]"
        min={0}
        max={options.length - 1}
        step={1}
        value={[selectedIndex]}
        disabled={disabled}
        onValueChange={([index]) => {
          const option = options[index];
          if (option) onChange(option.product);
        }}
      >
        <SliderPrimitive.Track className="relative h-1 w-full grow rounded-full bg-system-white12">
          <SliderPrimitive.Range className="absolute h-full rounded-full bg-[#9B93FF] transition-[left,right] duration-[450ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]" />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          aria-label={t('credits')}
          aria-valuetext={`${creditsLabel(options[selectedIndex])} ${t('credits')}`}
          className="block size-4 rounded-full bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9B93FF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#161722]"
        />
      </SliderPrimitive.Root>
    </div>
  );
};
